import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@core/components/ui/dialog";
import { Button } from "@core/components/ui/button";
import { Checkbox } from "@core/components/ui/checkbox";
import { Label } from "@core/components/ui/label";
import { Tag, Folder } from "lucide-react";
import { getAllTags, type Tag as TagType } from "@core/lib/storage/TagStorage";
import {
  getAllFolders,
  type Folder as FolderType,
} from "@core/lib/storage/FolderStorage";
import { getConversation } from "@core/lib/storage/ConversationStorageSimple";
import { useToast } from "@core/hooks/use-toast";

interface ManageTagsFolderDialogProps {
  conversationId: string;
  currentTags: string[];
  currentFolderId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  "data-testid"?: string;
}

export const ManageTagsFolderDialog: React.FC<ManageTagsFolderDialogProps> = ({
  conversationId,
  currentTags,
  currentFolderId,
  open,
  onOpenChange,
  onSuccess,
  "data-testid": dataTestId,
}) => {
  const { t } = useTranslation("polls");
  const { toast } = useToast();
  const [selectedTags, setSelectedTags] = useState<Set<string>>(
    new Set(currentTags),
  );
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    currentFolderId || null,
  );
  const [isSaving, setIsSaving] = useState(false);

  const allTags = getAllTags();
  const allFolders = getAllFolders();

  // Reset state when dialog opens/closes or conversation changes
  useEffect(() => {
    if (open) {
      setSelectedTags(new Set(currentTags));
      setSelectedFolderId(currentFolderId || null);
    }
  }, [open, currentTags, currentFolderId]);

  const handleTagToggle = (tagName: string) => {
    const newSelected = new Set(selectedTags);
    if (newSelected.has(tagName)) {
      newSelected.delete(tagName);
    } else {
      newSelected.add(tagName);
    }
    setSelectedTags(newSelected);
  };

  const handleFolderSelect = (folderId: string | null) => {
    setSelectedFolderId(folderId === selectedFolderId ? null : folderId);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Get the current conversation
      const conversation = getConversation(conversationId);
      if (!conversation) {
        toast({
          title: t("manage_tags_folder.error_title"),
          description: t("manage_tags_folder.conversation_not_found"),
          variant: "destructive",
        });
        return;
      }

      // Update tags
      const tagsArray = Array.from(selectedTags);

      // Update folderId in metadata
      const updatedMetadata = {
        ...conversation.metadata,
        folderId: selectedFolderId || undefined,
      };

      // Update conversation with tags and metadata
      // Since updateConversation hook only accepts UpdateConversationData which doesn't include metadata,
      // we need to update the conversation directly using storage
      const { updateConversation: updateConversationStorage } =
        await import("@core/lib/storage/ConversationStorageSimple");
      const updatedConversation = {
        ...conversation,
        tags: tagsArray,
        metadata: updatedMetadata,
        updatedAt: new Date(),
      };
      updateConversationStorage(updatedConversation);

      toast({
        title: t("manage_tags_folder.update_success_title"),
        description: t("manage_tags_folder.update_success_description"),
      });

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: t("manage_tags_folder.error_title"),
        description: t("manage_tags_folder.update_error_description"),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid={dataTestId}
      >
        <DialogHeader>
          <DialogTitle>{t("manage_tags_folder.title")}</DialogTitle>
          <DialogDescription>
            {t("manage_tags_folder.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4" onClick={(e) => e.stopPropagation()}>
          {/* Tags Section */}
          <div className="space-y-3">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Tag className="w-4 h-4" />
              {t("manage_tags_folder.tags_label")}
            </Label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {allTags.length === 0 ? (
                <p className="text-sm text-gray-400">
                  {t("manage_tags_folder.no_tags_available")}
                </p>
              ) : (
                allTags.map((tag: TagType) => (
                  <div
                    key={tag.id}
                    className="flex items-center space-x-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      id={`tag-${tag.id}`}
                      checked={selectedTags.has(tag.name)}
                      onCheckedChange={() => handleTagToggle(tag.name)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Label
                      htmlFor={`tag-${tag.id}`}
                      className="flex items-center gap-2 cursor-pointer flex-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span
                        className="inline-block w-3 h-3 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span>{tag.name}</span>
                    </Label>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Folder Section */}
          <div className="space-y-3">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Folder className="w-4 h-4" />
              {t("manage_tags_folder.folder_label")}
            </Label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {allFolders.length === 0 ? (
                <p className="text-sm text-gray-400">
                  {t("manage_tags_folder.no_folders_available")}
                </p>
              ) : (
                <>
                  <div
                    className="flex items-center space-x-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      id="folder-none"
                      checked={selectedFolderId === null}
                      onCheckedChange={() => handleFolderSelect(null)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Label
                      htmlFor="folder-none"
                      className="cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t("manage_tags_folder.no_folder")}
                    </Label>
                  </div>
                  {allFolders.map((folder: FolderType) => (
                    <div
                      key={folder.id}
                      className="flex items-center space-x-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        id={`folder-${folder.id}`}
                        checked={selectedFolderId === folder.id}
                        onCheckedChange={() => handleFolderSelect(folder.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Label
                        htmlFor={`folder-${folder.id}`}
                        className="flex items-center gap-2 cursor-pointer flex-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span>{folder.icon}</span>
                        <span>{folder.name}</span>
                      </Label>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
            data-testid="managetagsfolderdialog-button"
          >
            {t("manage_tags_folder.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            data-testid="save-manage-tags"
          >
            {isSaving
              ? t("manage_tags_folder.saving")
              : t("manage_tags_folder.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
